import {
  ChevronLeft as ChevronLeftIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";

// Импорт role-aware навигации
import { useNotifications } from "../../hooks/useNotifications";
import { NotificationType } from "../../types/notifications";
import { logger } from "../../utils/logger";
import {
  NavigationItem,
  NavigationSubItem,
  useRoleAwareNavigation,
} from "../navigation/RoleAwareNavigation";

const drawerWidth = 280;

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

// Старый статический массив navigationItems удален - теперь используется role-aware навигация

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, isMobile }) => {
  const theme = useTheme();
  const location = useLocation();
  const { i18n: _i18n } = useTranslation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Получаем навигацию, адаптированную под роль
  const { getNavigationItems, getCurrentRoleInfo } = useRoleAwareNavigation();
  const navigationItems = getNavigationItems();
  const currentRoleInfo = getCurrentRoleInfo();
  const { unreadCount, unreadByType } = useNotifications();

  const handleExpandClick = (path: string) => {
    setExpandedItems((prev) =>
      prev.includes(path)
        ? prev.filter((item) => item !== path)
        : [...prev, path],
    );
  };

  const isItemActive = (item: NavigationItem) => {
    if (location.pathname === item.path) return true;
    if (item.children) {
      return item.children.some((child) => location.pathname === child.path);
    }
    return location.pathname.startsWith(`${item.path}/`);
  };

  const isSubItemActive = (subItem: NavigationSubItem) => {
    return location.pathname === subItem.path;
  };

  const renderNavigationItem = (item: NavigationItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.path);
    const isActive = isItemActive(item);
    const handleItemClick = () => {
      try {
        logger.info("nav:click", { path: item.path, title: item.title });
      } catch {}
    };

    // Динамические badge-значения
    const computeBadge = () => {
      if (item.path === "/notifications") return unreadCount || 0;
      if (item.path.startsWith("/analytics")) {
        return (
          (unreadByType?.[NotificationType.PERFORMANCE_ALERT] as
            | number
            | undefined) || 0
        );
      }
      if (item.path.startsWith("/documents/reports")) {
        return (
          (unreadByType?.[NotificationType.ANALYTICS_REPORT_READY] as
            | number
            | undefined) || 0
        );
      }
      return item.badge;
    };

    return (
      <React.Fragment key={item.path}>
        <ListItem disablePadding>
          <ListItemButton
            component={hasChildren ? "div" : RouterLink}
            to={hasChildren ? undefined : item.path}
            onClick={
              hasChildren ? () => handleExpandClick(item.path) : handleItemClick
            }
            selected={isActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? "initial" : "center",
              px: 2.5,
              py: 1,
              borderRadius: "0 24px 24px 0",
              mx: 1,
              my: 0.5,
              "&.Mui-selected": {
                backgroundColor: "var(--color-primary-50)",
                color: "var(--color-primary-700)",
                "&:hover": {
                  backgroundColor: "var(--color-primary-100)",
                },
              },
              "&:hover": {
                backgroundColor: "var(--color-gray-50)",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : "auto",
                justifyContent: "center",
                color: isActive
                  ? "var(--color-primary-600)"
                  : "var(--color-gray-600)",
              }}
            >
              <Badge
                badgeContent={computeBadge()}
                color="error"
                variant={
                  computeBadge() && computeBadge()! > 0 ? "standard" : "dot"
                }
                invisible={!computeBadge()}
              >
                {item.icon}
              </Badge>
            </ListItemIcon>

            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: isActive ? 600 : 500 }}
                  >
                    {item.title}
                  </Typography>
                  {item.new && (
                    <Badge
                      badgeContent="NEW"
                      color="success"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.6rem",
                          height: 16,
                          minWidth: 16,
                        },
                      }}
                    />
                  )}
                </Box>
              }
              sx={{
                opacity: open ? 1 : 0,
                color: isActive
                  ? "var(--color-primary-700)"
                  : "var(--color-gray-700)",
              }}
            />

            {hasChildren && open && (
              <IconButton
                size="small"
                sx={{
                  color: isActive
                    ? "var(--color-primary-600)"
                    : "var(--color-gray-500)",
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </ListItemButton>
        </ListItem>

        {/* Submenu */}
        {hasChildren && (
          <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((subItem) => (
                <ListItem key={subItem.path} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={subItem.path}
                    selected={isSubItemActive(subItem)}
                    sx={{
                      pl: 6,
                      pr: 2.5,
                      py: 0.75,
                      mx: 1,
                      borderRadius: "0 20px 20px 0",
                      "&.Mui-selected": {
                        backgroundColor: "var(--color-primary-25)",
                        color: "var(--color-primary-700)",
                      },
                      "&:hover": {
                        backgroundColor: "var(--color-gray-25)",
                      },
                    }}
                  >
                    {subItem.icon && (
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: isSubItemActive(subItem)
                            ? "var(--color-primary-600)"
                            : "var(--color-gray-500)",
                        }}
                      >
                        <Badge
                          badgeContent={subItem.badge}
                          color="error"
                          variant="dot"
                          invisible={!subItem.badge}
                        >
                          {subItem.icon}
                        </Badge>
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            {subItem.title}
                          </Typography>
                          {subItem.new && (
                            <Badge
                              badgeContent="NEW"
                              color="success"
                              sx={{
                                "& .MuiBadge-badge": {
                                  fontSize: "0.5rem",
                                  height: 14,
                                  minWidth: 14,
                                },
                              }}
                            />
                          )}
                        </Box>
                      }
                      sx={{
                        color: isSubItemActive(subItem)
                          ? "var(--color-primary-700)"
                          : "var(--color-gray-600)",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}

        {item.divider && <Divider sx={{ my: 1, mx: 2 }} />}
      </React.Fragment>
    );
  };

  const drawer = (
    <>
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          borderBottom: "1px solid var(--color-gray-200)",
        }}
      >
        {open && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "var(--color-primary-500)",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                EO
              </Avatar>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "var(--color-gray-900)",
                  fontSize: "1.1rem",
                }}
              >
                Event Master
              </Typography>
            </Box>

            {/* Индикатор текущей роли */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 5 }}>
              <Box sx={{ color: `var(--color-${currentRoleInfo.color}-600)` }}>
                {currentRoleInfo.icon}
              </Box>
              <Chip
                label={currentRoleInfo.label}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.75rem",
                  height: 20,
                  borderColor: `var(--color-${currentRoleInfo.color}-500)`,
                  color: `var(--color-${currentRoleInfo.color}-700)`,
                  backgroundColor: `var(--color-${currentRoleInfo.color}-50)`,
                  "& .MuiChip-label": {
                    px: 1,
                  },
                }}
              />
            </Box>
          </Box>
        )}
        <IconButton
          onClick={onToggle}
          sx={{
            color: "var(--color-primary-600)",
            "&:hover": {
              backgroundColor: "var(--color-primary-50)",
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>

      <List component="nav" sx={{ pt: 2, pb: 1 }}>
        {navigationItems.map(renderNavigationItem)}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: {
          md: open ? drawerWidth : Number(theme.spacing(7).replace("px", "")),
        },
        flexShrink: { md: 0 },
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "1px solid var(--color-gray-200)",
              backgroundColor: "var(--color-background)",
            },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              borderRight: "1px solid var(--color-gray-200)",
              backgroundColor: "var(--color-background)",
              whiteSpace: "nowrap",
              overflowX: "hidden",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              width: open ? drawerWidth : theme.spacing(7),
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
